import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { siteInfo, aboutContent, faqData } from '@/mocks/site';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOSchema type="organization" />
      <SEOSchema
        type="webpage"
        data={{
          name: `关于我们 - ${siteInfo.name}`,
          description: '了解今日同人库的品牌故事、使命愿景与发展历程',
          url: `${siteInfo.url}/about`,
        }}
      />
      <SEOSchema
        type="faqpage"
        data={{
          questions: faqData.map((f) => ({ question: f.question, answer: f.answer })),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                {aboutContent.brandIntro.title}
              </h1>
              <p className="mt-3 text-foreground-600 text-sm md:text-base">
                从2024年至今，我们一直在为同人爱好者提供最优质的内容发现体验
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
              <div className="space-y-5">
                {aboutContent.brandIntro.paragraphs.map((p, i) => (
                  <p key={i} className="text-foreground-800 leading-relaxed text-sm md:text-base">
                    {p}
                  </p>
                ))}
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Anime%20style%20cozy%20creative%20studio%20with%20manga%20artists%20working%20together%2C%20warm%20sunlight%20through%20large%20windows%2C%20bookshelves%20filled%20with%20comics%20and%20art%20books%2C%20creative%20atmosphere%2C%20soft%20warm%20colors%2C%20Japanese%20anime%20art%20style%20illustration&width=800&height=500&seq=about-brand-intro&orientation=landscape"
                  alt="今日同人库团队"
                  title="今日同人库 - 同人爱好者社区"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {aboutContent.mission.title}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutContent.mission.items.map((item, i) => (
                <div key={i} className="p-6 bg-background-50 rounded-xl border border-background-200/70 text-center group transition-all duration-300 hover:-translate-y-1">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-100">
                    <i className={`${item.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation & Timeline */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {aboutContent.reputation.title}
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-14">
              {aboutContent.reputation.stats.map((stat, i) => (
                <div key={i} className="text-center p-6 bg-background-100 rounded-xl">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{stat.value}</div>
                  <div className="mt-1.5 text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-heading font-semibold text-foreground-950 mb-8 text-center">用户评价</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {aboutContent.reputation.testimonials.map((t, i) => (
                <div key={i} className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <p className="text-sm text-foreground-700 leading-relaxed italic">&ldquo;{t.quote}&rdquo;</p>
                  <div className="flex items-center gap-3 mt-5 pt-4 border-t border-background-200/70">
                    <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.author}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-heading font-semibold text-foreground-950 mb-8 mt-14 text-center">发展历程</h3>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-6">
                {aboutContent.timeline.map((item, i) => (
                  <div key={i} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-accent-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50"></div>
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className="inline-block px-4 py-2 bg-background-100 rounded-xl border border-background-200/70">
                        <span className="text-sm font-semibold text-accent-600 whitespace-nowrap">{item.year}</span>
                        <p className="mt-1 text-sm text-foreground-700">{item.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
            </div>

            <div className="space-y-3">
              {faqData.map((faq, i) => (
                <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500 text-lg"></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              联系我们
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base">
              有任何问题或建议？欢迎通过以下方式联系我们
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              <div className="p-6 bg-background-100 rounded-xl">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h4 className="mt-4 text-sm font-semibold text-foreground-950">邮箱</h4>
                <p className="mt-1 text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="p-6 bg-background-100 rounded-xl">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-map-pin-line text-xl text-accent-600"></i>
                </div>
                <h4 className="mt-4 text-sm font-semibold text-foreground-950">地址</h4>
                <p className="mt-1 text-sm text-foreground-600">{siteInfo.address}</p>
              </div>
              <div className="p-6 bg-background-100 rounded-xl">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-share-line text-xl text-secondary-600"></i>
                </div>
                <h4 className="mt-4 text-sm font-semibold text-foreground-950">社交媒体</h4>
                <div className="flex items-center justify-center gap-3 mt-2">
                  <a href={siteInfo.social.weibo} target="_blank" rel="nofollow noopener noreferrer" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博">
                    <i className="ri-weibo-line text-xl"></i>
                  </a>
                  <a href={siteInfo.social.bilibili} target="_blank" rel="nofollow noopener noreferrer" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" aria-label="哔哩哔哩">
                    <i className="ri-bilibili-line text-xl"></i>
                  </a>
                  <a href={siteInfo.social.lofter} target="_blank" rel="nofollow noopener noreferrer" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" aria-label="Lofter">
                    <i className="ri-link text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}